import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ApiService } from 'src/app/_services/api.service';
import { GlobalService } from 'src/app/_services/global.service';

@Component({
  selector: 'app-playlists',
  templateUrl: './playlists.component.html',
  styleUrls: ['./playlists.component.css'],
})
export class PlaylistsComponent implements OnInit {
  playlistsList: any[] = [];
  showArchived = false;
  loading = true;
  expandedRows: { [key: string]: boolean } = {};

  constructor(
    private apiService: ApiService,
    public globalService: GlobalService,
    public router: Router
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    const params: any = { archived: this.showArchived };

    forkJoin({
      playlists: this.apiService.post('kodi/playlistslistall', params),
      rules: this.apiService.post('kodi/ruleslistall', {}),
    })
      .pipe(
        switchMap((data: any) => {
          const playlists = data.playlists?.Result || [];
          const rules = data.rules?.Result || [];
          const missingMonitors = rules.filter(
            (r: any) => !Array.isArray(r.monitors) && r.id != null
          );

          if (!missingMonitors.length) {
            return of({ playlists, rules });
          }

          // Fallback: backend non ancora aggiornato — carica i monitor per ogni regola
          return forkJoin(
            missingMonitors.map((r: any) =>
              this.apiService.post('kodi/ruleslistall', { id: r.id }).pipe(
                map((res: any) => res?.Result?.[0] || r)
              )
            )
          ).pipe(
            map((detailed: any[]) => {
              const byId: { [key: string]: any } = {};
              detailed.forEach((rule) => {
                if (rule?.id != null) {
                  byId[rule.id] = rule;
                }
              });
              const merged = rules.map((r: any) =>
                byId[r.id] ? { ...r, monitors: byId[r.id].monitors || [] } : r
              );
              return { playlists, rules: merged };
            })
          );
        })
      )
      .subscribe(
        ({ playlists, rules }) => {
          this.playlistsList = this.attachMonitorsFromRules(playlists, rules);
          this.loading = false;
        },
        () => {
          this.playlistsList = [];
          this.loading = false;
        }
      );
  }

  /**
   * Associa i monitor alle playlist tramite le regole:
   * regola.playlist_id + regola.monitors → elenco monitor per playlist.
   */
  private attachMonitorsFromRules(playlists: any[], rules: any[]): any[] {
    const monitorsByPlaylist = new Map<number, Map<number, any>>();

    for (const rule of rules || []) {
      const playlistId = Number(rule.playlist_id);
      if (!playlistId) {
        continue;
      }

      if (!monitorsByPlaylist.has(playlistId)) {
        monitorsByPlaylist.set(playlistId, new Map());
      }
      const byMonitorId = monitorsByPlaylist.get(playlistId);

      for (const monitor of rule.monitors || []) {
        if (!monitor?.id) {
          continue;
        }
        const monitorId = Number(monitor.id);
        const existing = byMonitorId.get(monitorId);
        const regole = existing?.regole ? [...existing.regole] : [];
        if (rule.nome && !regole.includes(rule.nome)) {
          regole.push(rule.nome);
        }
        byMonitorId.set(monitorId, {
          ...(existing || monitor),
          ...monitor,
          regole,
          regole_label: regole.join(', '),
        });
      }
    }

    return (playlists || []).map((playlist) => {
      const playlistId = Number(playlist.id);
      const linkedMap = monitorsByPlaylist.get(playlistId);
      const linked = linkedMap ? Array.from(linkedMap.values()) : [];
      linked.sort((a, b) =>
        String(a.nome || a.descrizione || '').localeCompare(
          String(b.nome || b.descrizione || ''),
          'it'
        )
      );
      return {
        ...playlist,
        monitors: linked,
        monitors_count: linked.length,
      };
    });
  }

  changeArchived(): void {
    this.load();
  }

  apri(data: any): void {
    this.router.navigate(['/dashboard/monitors/playlist-edit/update/' + data.id]);
  }

  apriMonitor(monitor: any): void {
    this.router.navigate(['/dashboard/monitors/monitor-edit/update/' + monitor.id]);
  }

  onRowExpand(event: any): void {
    if (event?.data?.id != null) {
      this.expandedRows[event.data.id] = true;
    }
  }

  onRowCollapse(event: any): void {
    if (event?.data?.id != null) {
      delete this.expandedRows[event.data.id];
    }
  }
}
